/**
 * 2. 종합 진단 섹션
 * - 왼쪽: 내 상품 정보 + 핵심 지표 (7개 stat box) + 상품 태그
 * - 중간: 진단 점수, 키워드 경쟁력 Progress, 리뷰 건강도 Progress, 핵심 개선 포인트
 * - 오른쪽: AI 전문가 분석 요약 카드
 *
 * @param {object} report - 분석 리포트 데이터
 */
import { Space, Progress, Tag, Popover, Spin } from 'antd';
import { Typography } from 'antd';
import {
    DashboardOutlined,
    WalletOutlined,
    TrophyOutlined,
    CommentOutlined,
    HeartOutlined,
    SearchOutlined,
    StarFilled,
    ThunderboltFilled,
    RobotOutlined,
    MessageOutlined,
} from '@ant-design/icons';
const { Text: AntText } = Typography;

const DiagnosticSection = ({ report, aiLoading }) => {
    const diag = report.diagnostic;
    const compIdx = report.keywordInfo?.compIdx;
    const seasonAI = report.seasonalityAnalysis?.aiAnalysis;

    // --- 실시간 백그라운드 수집 여부 판별 ---
    const isUpdating =
        (report.keywords || []).some(k => k.rank === null) ||
        (report.relatedQueries || []).some(k => k.rank === null) ||
        (report.autocompleteKeywords || []).some(k => k.rank === null);

    // --- 키워드 경쟁력 ---
    const kwPercent = diag
        ? diag.kwCompetitiveness.score
        : compIdx === '낮음'
          ? 80
          : compIdx === '중간'
            ? 55
            : compIdx === '높음'
              ? 30
              : 0;
    const kwColor =
        kwPercent >= 70
            ? '#10b981'
            : kwPercent >= 45
              ? '#f59e0b'
              : kwPercent > 0
                ? '#ef4444'
                : '#94a3b8';
    const kwTagColor =
        kwPercent >= 70
            ? 'success'
            : kwPercent >= 45
              ? 'warning'
              : kwPercent > 0
                ? 'error'
                : 'default';
    const kwTagText =
        kwPercent >= 70 ? '좋음' : kwPercent >= 45 ? '보통' : kwPercent > 0 ? '주의' : '-';
    const kwDesc = (() => {
        const parts = [];
        if (seasonAI?.trend) parts.push(seasonAI.trend);
        if (compIdx)
            parts.push(
                compIdx === '높음' ? '경쟁 치열' : compIdx === '중간' ? '경쟁 보통' : '경쟁 낮음'
            );
        if (diag) {
            const top = diag.kwCompetitiveness.topKwCount ?? 0;
            const ranked = diag.kwCompetitiveness.rankedKwCount ?? 0;
            const total = diag.kwCompetitiveness.totalKwCount ?? 0;
            let text = `핵심(10위권) ${top}개 · 노출(30위권) ${ranked}개 / 추천키워드 ${total}개`;
            if (isUpdating) text += ' (수집 중...)';
            parts.push(text);
        }
        return parts.join(', ') || '경쟁 데이터 없음';
    })();

    // --- 리뷰 건강도 ---
    const reviewPercent = diag
        ? diag.reviewHealth.score
        : (() => {
              const s = report.product?.reviewScore || 0;
              return s > 0 ? Math.round((s / 5) * 100) : 0;
          })();
    const reviewColor =
        reviewPercent >= 70
            ? '#10b981'
            : reviewPercent >= 45
              ? '#f59e0b'
              : reviewPercent > 0
                ? '#ef4444'
                : '#94a3b8';
    const reviewTagColor =
        reviewPercent >= 70
            ? 'success'
            : reviewPercent >= 45
              ? 'warning'
              : reviewPercent > 0
                ? 'error'
                : 'default';
    const reviewTagText =
        reviewPercent >= 70
            ? '좋음'
            : reviewPercent >= 45
              ? '보통'
              : reviewPercent > 0
                ? '주의'
                : '-';
    const myScore = report.product?.reviewScore || 0;
    const reviewDesc = (() => {
        const parts = [`${myScore.toFixed(2)}점`];
        if (diag) {
            parts.push(`포토리뷰 ${diag.reviewHealth.photoRatioPct}%`);
            if (diag.reviewHealth.negRatioPct > 0)
                parts.push(`불만 ${diag.reviewHealth.negRatioPct}%`);
        }
        return parts.join(', ') || '리뷰 데이터 없음';
    })();

    // --- 핵심 개선 포인트 ---
    const myReviewCount = report.product?.reviewCount || 0;
    const avgReviewCount =
        diag?.reviewHealth.avgCompReviewCount ||
        (report.competitors?.length
            ? Math.round(
                  report.competitors.reduce((acc, c) => acc + (c.reviewCount || 0), 0) /
                      report.competitors.length
              )
            : 0);
    const myWishCount = report.product?.wishCount || 0;
    const avgWishCount =
        diag?.avgWishCount || parseInt((report.stats?.[5]?.value || '0').replace(/,/g, ''));

    const improvementPoints = (() => {
        const pts = [];
        // 리뷰 볼륨
        if (avgReviewCount > 0) {
            const diff = avgReviewCount - myReviewCount;
            pts.push(
                diff > 0
                    ? `리뷰를 ${diff.toLocaleString()}개 이상 늘리면 상위권 진입에 유리합니다.`
                    : '리뷰 수가 충분합니다. 리뷰 품질 관리에 집중하세요.'
            );
        }
        // 포토리뷰 비율
        if (diag && diag.reviewHealth.photoRatioPct < 30) {
            pts.push(
                `포토리뷰 비율이 ${diag.reviewHealth.photoRatioPct}%로 낮습니다. 포토리뷰 이벤트를 활용해보세요.`
            );
        }
        // 불만 리뷰
        if (diag && diag.reviewHealth.negRatioPct > 3) {
            pts.push(
                `1~2점 부정 리뷰 비율이 ${diag.reviewHealth.negRatioPct}%입니다. 주요 불만 원인을 파악하고 CS를 강화하세요.`
            );
        }
        // 찜 수
        if (avgWishCount > 0) {
            pts.push(
                myWishCount < avgWishCount
                    ? '찜 수가 평균 대비 부족합니다. 찜 이벤트나 할인 프로모션을 활용해보세요.'
                    : '찜 수는 양호합니다. 현재 전략을 유지하세요.'
            );
        }
        // 키워드 순위
        if (diag && diag.kwCompetitiveness.totalKwCount > 0) {
            if (diag.kwCompetitiveness.rankedKwCount === 0) {
                pts.push('30위권 내 키워드가 없습니다. 태그 및 상품명 키워드 최적화가 필요합니다.');
            } else if ((diag.kwCompetitiveness.topKwCount ?? 0) === 0) {
                pts.push('10위권 핵심 키워드가 없습니다. 상위 노출 키워드 확장이 필요합니다.');
            }
        }
        if (pts.length === 0) pts.push('분석 완료 후 개선 포인트가 표시됩니다.');
        return pts;
    })();

    // --- 시장 동향 텍스트 ---
    const marketTrendText =
        seasonAI?.summary ||
        (() => {
            const searchVol = report.stats?.[0]?.value;
            const compRatio = report.stats?.[2]?.value;
            if (!searchVol) return null;
            return `검색 수요(${searchVol}건)가 발생하는 시장으로, 경쟁강도 ${compRatio || '-'}입니다.`;
        })();
    const marketAdviceText = seasonAI?.advice || null;

    // --- 개선 포인트 텍스트 (AI 카드용) ---
    const avgCompScore = report.competitors?.length
        ? report.competitors.reduce((s, c) => s + (c.score || 0), 0) / report.competitors.length
        : 0;
    const aiImprovementText = (() => {
        const diff = avgReviewCount - myReviewCount;
        if (myScore > 0 && avgCompScore > 0 && myScore < avgCompScore - 0.1) {
            return `리뷰 평점(${myScore.toFixed(2)}점)이 상위 경쟁사 평균(${avgCompScore.toFixed(2)}점) 대비 낮습니다. 품질 개선 및 고객 응대를 강화하세요.`;
        }
        if (diff > 0) {
            return `상위 경쟁사 평균 대비 리뷰 수와 찜 수가 부족합니다. 초기 구매자에게 긍정적 경험을 유도하여 리뷰를 확보하는 이벤트나 상세페이지 내 핵심 강점을 부각하는 콘텐츠 배치를 권장합니다.`;
        }
        return `리뷰 수와 찜 수가 양호합니다. 상위 노출 유지를 위해 키워드 최적화와 광고 효율을 점검하세요.`;
    })();

    return (
        <>
            {/* 2. 종합 진단 섹션 */}
            <div className="section-title-premium">
                <DashboardOutlined /> 상품 종합 진단
            </div>

            <div className="diagnostic-card-group">
                {/* 왼쪽: 상품 정보 */}
                <div className="diag-card">
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                        <Popover
                            content={
                                report.product?.image ? (
                                    <img
                                        src={report.product.image}
                                        alt="상품 이미지"
                                        style={{
                                            width: 360,
                                            height: 360,
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                        }}
                                    />
                                ) : null
                            }
                            placement="right"
                            trigger="hover"
                            styles={{ body: { padding: 6 } }}
                        >
                            <img
                                src={report.product?.image}
                                style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    background: '#f8fafc',
                                    cursor: 'zoom-in',
                                    flexShrink: 0,
                                }}
                                alt="Product"
                            />
                        </Popover>
                        <div style={{ flex: 1 }}>
                            <Space size={4} style={{ marginBottom: 4 }}>
                                <span className="my-badge">MID: {report.product?.id}</span>
                                <span className="rank-badge">
                                    {report.product?.rank > 0
                                        ? `${report.product.rank}위`
                                        : '순위밖'}
                                </span>
                            </Space>
                            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>
                                {report.product?.title}
                            </div>
                            <AntText
                                type="secondary"
                                style={{ fontSize: 11, display: 'block', marginTop: 4 }}
                            >
                                {report.product?.store}
                            </AntText>
                            {report.product?.category && (
                                <AntText
                                    type="secondary"
                                    style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                                >
                                    {report.product.category}
                                </AntText>
                            )}
                            <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
                                채널 방문 통계:{' '}
                                {report.product?.channelVisit ? (
                                    <span
                                        style={{ color: '#0f172a', fontWeight: 600, fontSize: 13 }}
                                    >
                                        {Object.entries(report.product.channelVisit).map(
                                            ([k, v], i, arr) => (
                                                <span key={k}>
                                                    {k}:{' '}
                                                    <span style={{ color: '#6366f1' }}>
                                                        {typeof v === 'number'
                                                            ? v.toLocaleString()
                                                            : typeof v === 'object'
                                                              ? JSON.stringify(v)
                                                              : String(v)}
                                                    </span>
                                                    {i < arr.length - 1 ? ' / ' : ''}
                                                </span>
                                            )
                                        )}
                                    </span>
                                ) : (
                                    <Tag color="orange" style={{ fontSize: 9, padding: '0 4px' }}>
                                        미수집
                                    </Tag>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* 내 상품 핵심 지표 (2열 레이아웃, 아이콘 좌측 배치로 가독성 극대화) */}
                    <div className="diag-card-inner-stats">
                        <div className="inner-stat-box ib-price">
                            <div className="icon-wrap">
                                <WalletOutlined />
                            </div>
                            <div className="content">
                                <div className="label">현재 가격</div>
                                <div className="val">
                                    {(report.product?.price || 0).toLocaleString()}원
                                </div>
                            </div>
                        </div>
                        {(() => {
                            const myPrice = report.product?.price || 0;
                            const avgPrice = parseInt(
                                (report.stats?.[3]?.value || '0').replace(/,/g, '')
                            );
                            if (!avgPrice) return null;
                            const diff = myPrice - avgPrice;
                            const isHigh = diff > 0;
                            const color = isHigh ? '#ef4444' : '#10b981';
                            const sign = isHigh ? '+' : '';
                            return (
                                <div className="inner-stat-box ib-price-diff">
                                    <div className="icon-wrap" style={{ color }}>
                                        <WalletOutlined />
                                    </div>
                                    <div className="content">
                                        <div className="label">평균가 대비</div>
                                        <div className="val" style={{ color }}>
                                            {sign}
                                            {diff.toLocaleString()}원
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="inner-stat-box ib-rank">
                            <div className="icon-wrap">
                                <TrophyOutlined />
                            </div>
                            <div className="content">
                                <div className="label">현재 순위</div>
                                <div className="val">
                                    {report.product?.rank > 0
                                        ? `${report.product.rank}위`
                                        : report.raw?.product
                                          ? '순위밖'
                                          : '조회실패'}
                                </div>
                            </div>
                        </div>
                        <div className="inner-stat-box ib-wish">
                            <div className="icon-wrap">
                                <HeartOutlined />
                            </div>
                            <div className="content">
                                <div className="label">찜 수</div>
                                <div className="val">
                                    {(report.product?.wishCount || 0).toLocaleString()}개
                                </div>
                            </div>
                        </div>
                        <div className="inner-stat-box ib-review">
                            <div className="icon-wrap">
                                <CommentOutlined />
                            </div>
                            <div className="content">
                                <div className="label">전체 리뷰수</div>
                                <div className="val">
                                    {(report.product?.reviewCount || 0).toLocaleString()}개
                                </div>
                            </div>
                        </div>
                        <div className="inner-stat-box ib-score">
                            <div className="icon-wrap">
                                <StarFilled style={{ color: '#f59e0b' }} />
                            </div>
                            <div className="content">
                                <div className="label">전체 평점</div>
                                <div className="val">
                                    {(report.product?.reviewScore || 0).toFixed(2)}점
                                </div>
                            </div>
                        </div>
                        <div className="inner-stat-box ib-recent-review">
                            <div className="icon-wrap">
                                <MessageOutlined />
                            </div>
                            <div className="content">
                                <div className="label">최근 6개월 리뷰수</div>
                                <div className="val">
                                    {(report.product?.recentReviewCount || 0).toLocaleString()}개
                                </div>
                            </div>
                        </div>
                        <div className="inner-stat-box ib-recent-score">
                            <div className="icon-wrap">
                                <StarFilled style={{ color: '#f59e0b' }} />
                            </div>
                            <div className="content">
                                <div className="label">최근 6개월 평점</div>
                                <div className="val">
                                    {(report.product?.recentReviewScore || 0).toFixed(2)}점
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: 16 }}>
                        {(() => {
                            const tags = (report.product?.tags || '').split(',').filter(Boolean);
                            return (
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: '#9ca3af',
                                        marginBottom: 8,
                                    }}
                                >
                                    # 상품 태그{' '}
                                    <span
                                        style={{ fontSize: 12, color: '#7c3aed', fontWeight: 700 }}
                                    >
                                        ({tags.length}개)
                                    </span>
                                </div>
                            );
                        })()}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {(report.product?.tags || '')
                                .split(',')
                                .filter(Boolean)
                                .map(tag => (
                                    <Tag
                                        key={tag}
                                        style={{
                                            border: 'none',
                                            background: '#f5f3ff',
                                            color: '#7c3aed',
                                            borderRadius: 12,
                                            fontSize: 12,
                                            padding: '2px 10px',
                                        }}
                                    >
                                        #{tag.trim()}
                                    </Tag>
                                )) || (
                                <AntText type="secondary" style={{ fontSize: 12 }}>
                                    태그 정보 없음
                                </AntText>
                            )}
                        </div>
                    </div>
                </div>

                {/* 중간: 점수 및 상태 */}
                <div className="diag-card">
                    <div
                        style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}
                    >
                        <div style={{ textAlign: 'center' }}>
                            <div
                                style={{
                                    fontSize: 40,
                                    fontWeight: 900,
                                    color: '#0f172a',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                }}
                            >
                                {report.score || 0}
                                {isUpdating && (
                                    <Spin size="small" style={{ transform: 'scale(0.8)' }} />
                                )}
                            </div>
                            <div style={{ fontSize: 14, color: '#3b82f6', fontWeight: 700 }}>
                                {report.score >= 80
                                    ? '우수'
                                    : report.score >= 60
                                      ? '양호'
                                      : '개선필요'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                                {report.score >= 80
                                    ? '최적화가 매우 잘 되어 있습니다'
                                    : report.score >= 60
                                      ? '양호하지만 개선 여지가 있습니다'
                                      : '긴급한 최적화가 필요합니다'}
                            </div>
                            <div
                                style={{
                                    fontSize: 13,
                                    color: '#64748b',
                                    marginTop: 6,
                                    lineHeight: 1.6,
                                }}
                            >
                                리뷰{' '}
                                <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                                    {(report.product?.reviewCount || 0).toLocaleString()}
                                </span>{' '}
                                / 상위10 평균{' '}
                                <span style={{ fontWeight: 700, color: '#0f172a' }}>
                                    {avgReviewCount.toLocaleString()}
                                </span>
                                <br />찜{' '}
                                <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                                    {(report.product?.wishCount || 0).toLocaleString()}
                                </span>{' '}
                                / 상위10 평균{' '}
                                <span style={{ fontWeight: 700, color: '#0f172a' }}>
                                    {report.stats?.[5]?.value || '0'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: 20 }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 8,
                            }}
                        >
                            <div
                                className="card-sub-title"
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                            >
                                <SearchOutlined /> 키워드 경쟁력
                                {isUpdating && (
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: '#3b82f6',
                                            fontWeight: 600,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            marginLeft: 8,
                                        }}
                                    >
                                        <Spin size="small" style={{ fontSize: 9 }} />
                                        업데이트 중...
                                    </span>
                                )}
                            </div>
                            {compIdx ? (
                                <Tag
                                    color={kwTagColor}
                                    style={{ fontSize: 12, padding: '2px 8px' }}
                                >
                                    {kwTagText}
                                </Tag>
                            ) : (
                                <Tag color="default" style={{ fontSize: 12, padding: '2px 8px' }}>
                                    -
                                </Tag>
                            )}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                            {kwDesc}
                        </div>
                        <Progress
                            percent={kwPercent}
                            strokeColor={kwColor}
                            showInfo={false}
                            size={['100%', 10]}
                        />
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 8,
                            }}
                        >
                            <div className="card-sub-title">
                                <StarFilled style={{ color: '#f59e0b' }} /> 리뷰 건강도
                            </div>
                            <Tag
                                color={reviewTagColor}
                                style={{ fontSize: 12, padding: '2px 8px' }}
                            >
                                {reviewTagText}
                            </Tag>
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                            {reviewDesc}
                        </div>
                        <Progress
                            percent={reviewPercent}
                            strokeColor={reviewColor}
                            showInfo={false}
                            size={['100%', 10]}
                        />
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
                        <div className="card-sub-title" style={{ marginBottom: 12 }}>
                            <ThunderboltFilled style={{ color: '#f59e0b' }} /> 핵심 개선 포인트
                        </div>
                        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.8 }}>
                            {improvementPoints.map((pt, i) => (
                                <div key={i}>
                                    {i + 1}. {pt}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 오른쪽: AI 전략 요약 */}
                <div className="diag-card ai-strategy-card">
                    <div className="ai-strategy-header">
                        <div className="ai-strategy-title" style={{ color: '#0f172a' }}>
                            <RobotOutlined style={{ color: '#4f46e5' }} /> AI 전문가 분석 요약
                        </div>
                    </div>

                    <div className="ai-content-scroll">
                        {isUpdating ? (
                            <div
                                style={{
                                    padding: '60px 20px',
                                    textAlign: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 12,
                                }}
                            >
                                <Spin size="large" style={{ color: '#3b82f6' }} />
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: '#3b82f6',
                                        fontWeight: 700,
                                        marginTop: 8,
                                    }}
                                >
                                    실시간 키워드 분석 중
                                </div>
                                <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
                                    추천 키워드들의 검색량과 네이버 쇼핑 순위를
                                    <br />
                                    정밀 수집하고 있습니다. 잠시만 기다려주세요.
                                </div>
                            </div>
                        ) : aiLoading ? (
                            <div
                                style={{
                                    padding: '60px 20px',
                                    textAlign: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 12,
                                }}
                            >
                                <Spin size="large" style={{ color: '#4f46e5' }} />
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: '#4f46e5',
                                        fontWeight: 700,
                                        marginTop: 8,
                                    }}
                                >
                                    AI 맞춤 컨설팅 리포트 생성 중
                                </div>
                                <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
                                    수집 완료된 실시간 순위를 완벽하게 반영한
                                    <br />
                                    맞춤 진단 리포트를 생성하고 있습니다.
                                </div>
                            </div>
                        ) : report.expertAnalysis ? (
                            <>
                                <div className="ai-insight-section">
                                    <div className="ai-insight-title" style={{ color: '#4f46e5' }}>
                                        📈 시장 동향
                                    </div>
                                    <div className="ai-insight-text">
                                        {report.expertAnalysis.marketTrend}
                                    </div>
                                </div>
                                <div className="ai-insight-section">
                                    <div className="ai-insight-title" style={{ color: '#10b981' }}>
                                        💰 가격 전략
                                    </div>
                                    <div className="ai-insight-text">
                                        {report.expertAnalysis.priceStrategy}
                                    </div>
                                </div>
                                <div className="ai-insight-section">
                                    <div className="ai-insight-title" style={{ color: '#3b82f6' }}>
                                        🔑 키워드 전략
                                    </div>
                                    <div className="ai-insight-text">
                                        {report.expertAnalysis.keywordStrategy}
                                    </div>
                                </div>
                                <div className="ai-insight-section">
                                    <div className="ai-insight-title" style={{ color: '#f59e0b' }}>
                                        ⭐ 리뷰 전략
                                    </div>
                                    <div className="ai-insight-text">
                                        {report.expertAnalysis.reviewStrategy}
                                    </div>
                                </div>
                                <div className="ai-insight-section">
                                    <div className="ai-insight-title" style={{ color: '#8b5cf6' }}>
                                        🏆 경쟁 우위
                                    </div>
                                    <div className="ai-insight-text">
                                        {report.expertAnalysis.competitive}
                                    </div>
                                </div>
                                {report.expertAnalysis.actionPlan?.length > 0 && (
                                    <div className="ai-insight-section">
                                        <div
                                            className="ai-insight-title"
                                            style={{ color: '#ef4444' }}
                                        >
                                            🎯 종합 액션플랜
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 8,
                                                marginTop: 6,
                                            }}
                                        >
                                            {report.expertAnalysis.actionPlan.map((action, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        display: 'flex',
                                                        gap: 8,
                                                        alignItems: 'flex-start',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            flexShrink: 0,
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: '50%',
                                                            background:
                                                                i === 0
                                                                    ? '#ef4444'
                                                                    : i === 1
                                                                      ? '#f59e0b'
                                                                      : '#6366f1',
                                                            color: '#fff',
                                                            fontSize: 11,
                                                            fontWeight: 800,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {i + 1}
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            color: '#475569',
                                                            lineHeight: 1.6,
                                                        }}
                                                    >
                                                        {action}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {marketTrendText && (
                                    <div className="ai-insight-section">
                                        <div
                                            className="ai-insight-title"
                                            style={{ color: '#4f46e5' }}
                                        >
                                            📈 시장 동향
                                        </div>
                                        <div className="ai-insight-text">
                                            {marketTrendText}
                                            {marketAdviceText && (
                                                <>
                                                    <br />
                                                    {marketAdviceText}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className="ai-insight-section">
                                    <div className="ai-insight-title" style={{ color: '#059669' }}>
                                        🎯 개선 포인트
                                    </div>
                                    <div className="ai-insight-text">{aiImprovementText}</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default DiagnosticSection;
